import { ActivatedRoute, Router } from '@angular/router';
import { faFileExport, faTrashCan, faWindowRestore } from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';

import { PopupNotificationType, SPECIFIC_DOCUMENT_GROUPS } from '../../constants';
import { TDocumentIdKey, UploadedFileContextMenuOption } from '../../constants/types';
import { DocumentIdKey } from '../../projects/edit-project/constants';
import { IAssignDocument } from '../../projects/models/IAssignDocument';
import { IDocument, IDocumentActionsAllowed } from '../../projects/models/IDocument';
import { IInvalidDocumentsAndDocumentGroups } from '../../projects/models/IInvalidDocumentsAndDocumentGroups';
import { IRequiredDocumentCategory } from '../../projects/models/IRequiredDocumentCategory';
import { IRequiredDocumentGroup } from '../../projects/models/IRequiredDocumentGroup';
import { DocumentsService } from '../../projects/services/documents.service';
import { FileUtils } from './file.utils';
import { ModalUtils } from './modal.utils';

interface IDocumentContextMenuHandlers {
    onAssign?: (event: MouseEvent) => void | Promise<void>;
    onSplit?: (event: MouseEvent) => void | Promise<void>;
    onDelete?: (event?: MouseEvent) => void | Promise<void>;
    onVersions?: (event: MouseEvent) => void | Promise<void>;
}

export enum DocumentGroupWarningSeverity {
    Danger = 'Danger',
    Warning = 'Warning'
}

export enum DocumentGroupWarningType {
    InvalidGroup = 'InvalidGroup',
    InvalidDocument = 'InvalidDocument',
    NewDocument = 'NewDocument'
}

export interface IDocumentGroupWarning {
    severity: DocumentGroupWarningSeverity;
    type: DocumentGroupWarningType;
    message: string;
    isDocumentRelated: boolean;
}

export class DocumentUtils {
    public static defaultDocumentGroupWarningMessages: Record<DocumentGroupWarningType, string> = {
        [DocumentGroupWarningType.InvalidGroup]: 'documents.required_documents.group_marked_invalid',
        [DocumentGroupWarningType.InvalidDocument]: 'documents.required_documents.group_contains_invalid_documents',
        [DocumentGroupWarningType.NewDocument]: 'documents.required_documents.group_contains_new_documents'
    };

    public static areThereEnabledDocumentGroups(documentSvc: DocumentsService): boolean {
        const reqDocs = documentSvc.getCachedRequiredDocuments();
        if (reqDocs) {
            const enabledDocGroup = reqDocs.categories.find((c) => c.groups.find((g) => !g.isDisabled));

            return !!enabledDocGroup;
        }

        return false;
    }

    public static areThereNewDocumentsAdded(documentSvc: DocumentsService): boolean {
        const reqDocs = documentSvc.getCachedRequiredDocuments();
        if (reqDocs) {
            const areThereNewDocsAdded = !!reqDocs.categories.find((c) =>
                c.groups.find((g) => g.assignedDocuments.find((d) => d.isNew))
            );
            if (areThereNewDocsAdded) {
                return true;
            }
        }

        return false;
    }

    public static areThereTooBigFiles(documentSvc: DocumentsService): boolean {
        const reqDocs = documentSvc.getCachedRequiredDocuments();
        if (reqDocs) {
            const areTooBigFilesAdded = !!reqDocs.categories.find((c) =>
                c.groups.find((g) => g.assignedDocuments.find((d) => d.isTooBig))
            );
            if (areTooBigFilesAdded) {
                return true;
            }
        }

        return false;
    }

    public static areThereAnyDocumentsUploaded(documentSvc: DocumentsService): boolean {
        const reqDocs = documentSvc.getCachedRequiredDocuments();
        if (reqDocs) {
            const areThereAnyDocsAdded = !!reqDocs.categories.find((c) =>
                c.groups.find((g) => !!g.assignedDocuments.length)
            );
            if (areThereAnyDocsAdded) {
                return true;
            }
        }

        return false;
    }

    public static isNewValidDocument(document: IDocument, ignoreValidity = false): boolean {
        return document.isNew && (document.isValid || ignoreValidity) && !document.isTooBig;
    }

    public static areThereAnyNewValidDocuments(documentSvc: DocumentsService, ignoreValidity = false): boolean {
        const reqDocs = documentSvc.getCachedRequiredDocuments();
        if (!!reqDocs) {
            const areThereAnyNewValidDocuments = !!reqDocs.categories.find((c) =>
                c.groups.find(
                    (g) =>
                        !g.isDisabled &&
                        !!g.assignedDocuments.find((d) => DocumentUtils.isNewValidDocument(d, ignoreValidity))
                )
            );
            if (areThereAnyNewValidDocuments) {
                return true;
            }
        }

        return false;
    }

    public static isNotCategorizedDocumentCategory(category: IRequiredDocumentCategory): boolean {
        if (!category?.groups?.length) {
            return false;
        }

        return category.groups.length === 1 && category.groups[0].groupType === SPECIFIC_DOCUMENT_GROUPS.NotCategorized;
    }

    public static async assignDocumentsToGroups(
        documentSvc: DocumentsService,
        projectId: number,
        payload: IAssignDocument[]
    ): Promise<{
        documents?: IDocument[];
        messageType: PopupNotificationType;
        messageTitle: string;
        messageContentKey: string;
        errorMessage?: string;
        hasAnyDocumentUnassignedFromNotCategorizedGroup: boolean;
    }> {
        const messageTitle = 'documents.assign_document_title';
        const originalDocumentGroups: Record<string, { name: string; lenderId: number }> = {};
        let hasAnyDocumentUnassignedFromNotCategorizedGroup = false;

        for (const document of payload) {
            const documentIdKey = DocumentUtils.getDocumentIdKey({
                documentId: document.documentId,
                hypoDossierDocumentId: document.hypoDossierDocumentId
            } as IDocument);
            const documentIdValue = document[documentIdKey];
            const movedDocument = documentSvc.removeDocumentFromRequiredDocumentCache(documentIdValue, documentIdKey);

            if (movedDocument) {
                documentSvc.addDocumentToRequiredDocumentsCache(
                    {
                        ...movedDocument,
                        documentGroup: document.group,
                        lenderId: document.lenderId
                    },
                    documentIdKey === DocumentIdKey.DocumentId,
                    documentIdKey
                );
                originalDocumentGroups[documentIdValue] = {
                    name: movedDocument.documentGroup,
                    lenderId: movedDocument.lenderId
                };

                if (!hasAnyDocumentUnassignedFromNotCategorizedGroup) {
                    hasAnyDocumentUnassignedFromNotCategorizedGroup =
                        movedDocument.documentGroup === SPECIFIC_DOCUMENT_GROUPS.NotCategorized;
                }
            }
        }

        try {
            const documents = await documentSvc.assignDocumentsToGroups(projectId, payload);
            for (const document of documents) {
                const documentIdKey = DocumentUtils.getDocumentIdKey(document);
                documentSvc.updateDocumentInRequiredDocumentsCache(document, documentIdKey);
            }

            return {
                documents,
                messageType: PopupNotificationType.success,
                messageTitle,
                messageContentKey: 'documents.assign_document_successful',
                hasAnyDocumentUnassignedFromNotCategorizedGroup
            };
        } catch (ex) {
            console.error(ex);

            for (const document of payload) {
                const documentIdKey = DocumentUtils.getDocumentIdKey({
                    documentId: document.documentId,
                    hypoDossierDocumentId: document.hypoDossierDocumentId
                } as IDocument);
                const documentIdValue = document[documentIdKey];
                const movedDocument = documentSvc.removeDocumentFromRequiredDocumentCache(
                    documentIdValue,
                    documentIdKey
                );

                if (movedDocument) {
                    documentSvc.addDocumentToRequiredDocumentsCache(
                        {
                            ...movedDocument,
                            documentGroup: originalDocumentGroups[documentIdValue].name,
                            lenderId: originalDocumentGroups[documentIdValue].lenderId
                        },
                        documentIdKey === DocumentIdKey.DocumentId,
                        documentIdKey
                    );
                }
            }

            return {
                messageType: PopupNotificationType.error,
                messageTitle,
                messageContentKey: 'documents.assign_document_failed',
                errorMessage: ex.error?.message,
                hasAnyDocumentUnassignedFromNotCategorizedGroup: false
            };
        }
    }

    public static navigateByUrlAfterClickToDocument = (
        router: Router,
        activatedRoute: ActivatedRoute,
        id: number | 'NULL'
    ): void => {
        if (!id) {
            return;
        }

        const { documentId } = activatedRoute.snapshot.params;

        if (documentId) {
            if (id === 'NULL') {
                void router.navigate(['../'], { relativeTo: activatedRoute });
                return;
            } else {
                void router.navigateByUrl(router.url.replace(documentId, id.toString()));
                return;
            }
        }

        if (id === 'NULL') {
            return;
        }
        void router.navigate(['./', id], { relativeTo: activatedRoute });
    };

    public static getInvalidDocumentsAndDocumentGroups(
        documentSvc: DocumentsService
    ): IInvalidDocumentsAndDocumentGroups {
        const requiredDocuments = documentSvc.getCachedRequiredDocuments();
        const invalidDocuments: IDocument[] = [];
        const invalidDocumentGroups: IRequiredDocumentGroup[] = [];

        if (requiredDocuments?.categories?.length) {
            requiredDocuments.categories.forEach((category) => {
                if (category.groups?.length) {
                    category.groups.forEach((group) => {
                        if (group.assignedDocuments?.length) {
                            invalidDocuments.push(...group.assignedDocuments.filter((document) => !document.isValid));
                        }

                        if (!group.isValid) {
                            invalidDocumentGroups.push(group);
                        }
                    });
                }
            });
        }

        return { invalidDocuments, invalidDocumentGroups };
    }

    public static doDocumentsNeedActionBeforeLeaving(documentsSvc: DocumentsService): boolean {
        const areThereDocGroupsEnabled = DocumentUtils.areThereEnabledDocumentGroups(documentsSvc);
        if (!areThereDocGroupsEnabled) {
            return false;
        }

        const areThereNewDocsAdded = DocumentUtils.areThereAnyNewValidDocuments(documentsSvc);
        if (areThereNewDocsAdded) {
            return true;
        }

        return false;
    }

    public static confirmIfShouldProceedWithNavigatingAway(
        documentsSvc: DocumentsService,
        modalSvc: NzModalService,
        translate: TranslateService
    ): Promise<boolean> {
        const doDocumentsNeedActionBeforeLeaving = DocumentUtils.doDocumentsNeedActionBeforeLeaving(documentsSvc);
        if (doDocumentsNeedActionBeforeLeaving) {
            const { isConfirmedPromise } = ModalUtils.createDocumentsNotSubmittedConfirmation(modalSvc, translate);
            return isConfirmedPromise;
        }

        return Promise.resolve(true);
    }

    public static getActionsAllowedForDocument(document: IDocument): IDocumentActionsAllowed {
        const canPerformMutableActions = !!document && !document.isImmutable;

        return {
            canAssign: canPerformMutableActions,
            canSplit: canPerformMutableActions,
            canDelete: canPerformMutableActions,
            canShowVersions: !!document?.documentVersions?.length
        };
    }

    public static async getFileBlobContent(document: IDocument, documentsSvc: DocumentsService): Promise<Blob> {
        const { documentContent, documentMimeType } = document;

        if (documentContent) {
            return FileUtils.base64toBlob(documentContent, documentMimeType);
        } else {
            const { projectId, documentId } = document;

            const response = await documentsSvc.downloadDocumentBlob(projectId, documentId);
            return new Blob([response], { type: documentMimeType });
        }
    }

    public static isHypoDossierDocument(document: IDocument): boolean {
        return !!document?.hypoDossierDocumentId;
    }

    public static getDocumentIdKey(document: IDocument): TDocumentIdKey {
        return DocumentUtils.isHypoDossierDocument(document)
            ? DocumentIdKey.HypoDossierDocumentId
            : DocumentIdKey.DocumentId;
    }

    public static buildDocumentContextMenuItems(
        actionsAllowed: IDocumentActionsAllowed,
        handlers: IDocumentContextMenuHandlers
    ): UploadedFileContextMenuOption[] {
        const actions: UploadedFileContextMenuOption[] = [];
        if (!actionsAllowed) {
            return actions;
        }

        const canAssign = !!(actionsAllowed.canAssign && handlers.onAssign);
        const canDelete = !!(actionsAllowed.canDelete && handlers.onDelete);

        if (canAssign) {
            actions.push({
                value: 0,
                label: 'global.assign_document',
                icon: faChevronRight,
                onClick: handlers.onAssign
            });
        }

        if (actionsAllowed.canSplit && handlers.onSplit && (canAssign || canDelete)) {
            actions.push({
                value: 1,
                label: 'global.split_document',
                icon: faFileExport,
                onClick: handlers.onSplit
            });
        }

        if (canDelete) {
            actions.push({
                value: 2,
                label: 'global.delete',
                icon: faTrashCan,
                onClick: handlers.onDelete
            });
        }

        if (actionsAllowed.canShowVersions && handlers.onVersions && actions.length) {
            actions.unshift({
                value: 3,
                label: 'global.document_versions',
                icon: faWindowRestore,
                onClick: handlers.onVersions
            });
        }

        return actions;
    }

    public static getDocumentGroupWarning(
        group: IRequiredDocumentGroup,
        isNotRecognizedCategory: boolean,
        ignoreDocumentsValidity: boolean,
        displayCcInvalidGroupComment = true,
        invalidGroupMessage = DocumentUtils.defaultDocumentGroupWarningMessages.InvalidGroup,
        invalidDocumentMessage = DocumentUtils.defaultDocumentGroupWarningMessages.InvalidDocument,
        newDocumentMessage = DocumentUtils.defaultDocumentGroupWarningMessages.NewDocument
    ): IDocumentGroupWarning {
        if (!group || group.isDisabled) {
            return null;
        }

        if (!group.isValid) {
            return {
                severity: DocumentGroupWarningSeverity.Danger,
                type: DocumentGroupWarningType.InvalidGroup,
                message: displayCcInvalidGroupComment
                    ? group.competenceCenterComment || invalidGroupMessage
                    : invalidGroupMessage,
                isDocumentRelated: false
            };
        } else if (
            !ignoreDocumentsValidity &&
            !isNotRecognizedCategory &&
            group.assignedDocuments?.some((document) => !document.isValid)
        ) {
            return {
                severity: DocumentGroupWarningSeverity.Danger,
                type: DocumentGroupWarningType.InvalidDocument,
                message: invalidDocumentMessage,
                isDocumentRelated: true
            };
        } else if (group.assignedDocuments?.some((document) => DocumentUtils.isNewValidDocument(document))) {
            return {
                severity: DocumentGroupWarningSeverity.Warning,
                type: DocumentGroupWarningType.NewDocument,
                message: newDocumentMessage,
                isDocumentRelated: true
            };
        }

        return null;
    }
}
